import React, { useEffect, useState } from 'react'
import {  getInitialLang } from '../Common'
import { $MAX_WIDTH } from '../Consts'
import { checkIfHasInvestorAccess } from './support/checkIfHasInvestorAccess'
import { createToken } from './support/createToken'

const NAV_HEIGHT = 56

export const IconClose = ({
  color = '#fff',
  size = 26,
}: {
  color?: string
  size?: number
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

export const IconSlides = ({
  color = 'rgb(50, 153, 255)',
  size = 28,
}: {
  color?: string
  size?: number
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="15" height="10" rx="2" ry="2" opacity="0.35" />
    <rect x="5" y="6" width="15" height="10" rx="2" ry="2" opacity="0.65" />
    <rect x="7" y="8" width="15" height="10" rx="2" ry="2" />
  </svg>
)

export const IconLightbulb = ({
  color = '#0061FF',
  size = 28,
}: {
  color?: string
  size?: number
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <path d="M9 18h6" />
    <path d="M10 22h4" />
    <path d="M12 2a7 7 0 00-7 7c0 2.4 1.2 4.6 3 6v3a1 1 0 001 1h6a1 1 0 001-1v-3c1.8-1.4 3-3.6 3-6a7 7 0 00-7-7z" />
  </svg>
)

export const IconDollar = ({
  color = '#0061FF',
  size = 28,
}: {
  color?: string
  size?: number
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <path d="M3 3v18h18" />
    <polyline points="6 15 11 10 14 13 20 7" />
    <path d="M20 7v4m0-4h-4" />
  </svg>
)

export const IconDownload = ({
  color = '#0061FF',
  size = 22,
}: {
  color?: string
  size?: number
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <polyline points="7 11 12 16 17 11" />
    <line x1="12" y1="16" x2="12" y2="4" />
    <path d="M21 16v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
  </svg>
)

export const IconProduct = ({
  color = '#0061FF',
  size = 28,
}: {
  color?: string
  size?: number
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <polygon points="12 2 15.1 8.3 22 9.3 17 14.1 18.2 21 12 17.7 5.8 21 7 14.1 2 9.3 8.9 8.3 12 2" />
  </svg>
)

const s: Record<string, React.CSSProperties> = {
  page: {
    width: '100%',
    minHeight: '100vh',
    boxSizing: 'border-box',
    padding: `calc(${NAV_HEIGHT}px + 3vh) 1rem 3vh`,
    color: '#09213D',
    fontFamily:
      'Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    width: '100%',
    maxWidth: $MAX_WIDTH,
    background: '#fff',
    borderRadius: 18,
    padding: '2.5rem 4rem 3rem',
    boxShadow:
      '0 12px 32px rgba(0,0,0,.14),0 24px 64px rgba(0,0,0,.12)',
    display: 'flex',
    flexDirection: 'column',
    gap: '2.5rem',
    flex: 1,
    maxHeight: '100%',
    overflow: 'auto',
    boxSizing: 'border-box',
  },
  h1: {
    fontSize: '2.5rem',
    marginBottom: 0,
    fontWeight: 800,
    background:
      'linear-gradient(90deg, #45A6FF 0%, #1F8BFF 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.6rem',
  },
  p: {
    fontSize: '1.125rem',
    lineHeight: 1.6,
    textAlign: 'center',
    maxWidth: 720,
    margin: '0 auto',
    color: '#1B2C48',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: '0.5rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
    gap: '1.75rem',
    marginTop: '.5rem',
  },
  feat: {
    display: 'flex',
    alignItems: 'center',
    gap: '.8rem',
    fontSize: '1rem',
    lineHeight: 1.5,
    background: '#FFFFFF',
    padding: '1.3rem 1.2rem',
    borderRadius: 14,
    boxShadow: '0 6px 20px rgba(0,0,0,.06)',
    color: '#1B2C48',
  },
  btnRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1.25rem',
    justifyContent: 'center',
    marginTop: '1rem',
  },
  btn: {
    padding: '1rem 3rem',
    fontSize: '1.075rem',
    fontWeight: 700,
    borderRadius: 10,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    border: 0,
    transition: 'transform .15s ease,box-shadow .15s ease',
    gap: '.55rem',
  },
  primary: {
    background:
      'linear-gradient(135deg,#0A7CFF 0%,#0061FF 100%)',
    color: '#fff',
    boxShadow: '0 4px 16px rgba(10,124,255,.45)',
  },
  outline: {
    background: 'transparent',
    color: '#0061FF',
    border: '2px solid #0061FF',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,.65)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3vh 3vw',
    zIndex: 10000,
  },
  modal: {
    position: 'relative',
    width: 'min(94vw,1200px)',
    height: 'min(88vh,820px)',
    borderRadius: 14,
    overflow: 'hidden',
    background: '#000',
    boxShadow: '0 12px 48px rgba(0,0,0,.35)',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  close: {
    position: 'absolute',
    top: 14,
    right: 14,
    background: 'rgba(0,0,0,.45)',
    border: 'none',
    borderRadius: '50%',
    padding: 6,
    cursor: 'pointer',
    lineHeight: 0,
  },
}

const mobileCSS = `
@media (max-width: 640px) {
  .pd-page {
    display: block !important;
    height: auto !important;
    min-height: auto !important;
    padding: ${NAV_HEIGHT + 16}px 1rem 2rem !important;
  }
  .pd-card {
    width: 100% !important;
    max-height: none !important;
    padding: 3rem 1.5rem !important;
    box-sizing: border-box;
  }
  .pd-grid {
    grid-template-columns: 1fr !important;
  }
}
`

const EN = {
  title: 'BusinessA Pitch Deck',
  desc: "A quick, investor-focused deck that lets you judge our solution's upside and execution readiness at a glance",
  features: [
    'Team, product, value prop & a compelling market opportunity',
    'Solution highlights, competitor analysis & five year vision',
    'Business model, moats, profit forecasts & funding ask',
  ],
  view: 'View',
  download: 'Download',
  fileName: 'ba_pitch_deck.pdf',
}

const ZH = {
  title: 'BusinessA 融资路演资料',
  desc: '通过简洁的演示了解我们的市场潜力、产品差异化以及财务预测。',
  features: [
    '具有吸引力的市场机会概览',
    '差异化的解决方案与产品亮点',
    '商业模式与财务预测',
  ],
  view: '查看',
  download: '下载',
  fileName: 'ba_pitch_deck_cn.pdf',
}

const PDFS: Record<'en' | 'zh', string> = {
  en: 'https://serve.businessa.com/ba_pitch_deck.pdf',
  zh: 'https://serve.businessa.com/ba_pitch_deck_cn.pdf',
}

const LANGS = { en: EN, zh: ZH }

const BusinessPitchDeck: React.FC = () => {
  const [lang] = useState<'en' | 'zh'>(getInitialLang)
  const [open, setOpen] = useState(false)
  const [hasInvestorAccess] = useState<boolean>(() => checkIfHasInvestorAccess())

  useEffect(() => {
    if (hasInvestorAccess) return

    const authUrl = new URL('/auth.html', window.location.origin)
    authUrl.searchParams.set('url', window.location.href)

    window.location.replace(authUrl.href)
  }, [hasInvestorAccess])

  if (!hasInvestorAccess) return null

  const t = LANGS[lang]
  const pdfURL = PDFS[lang] + '?token=' + createToken()
  const icons = [
    <IconLightbulb size={24} />,
    <IconProduct size={24} />,
    <IconDollar size={24} />,
  ]
  const features = t.features.map((text, i) => ({
    icon: icons[i],
    text,
  }))

  const download = () => {
    const a = document.createElement('a')
    a.href = pdfURL + '&download=1'
    a.download = t.fileName
    a.target = '_blank'
    a.click()
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: mobileCSS }} />
      <div style={s.page} className="pd-page">
        <div style={s.card} className="pd-card">
          <h1 style={s.h1}>
            <div style={{ marginTop: 14 }}>
              <IconSlides size={38} />
            </div>
            {t.title}
          </h1>
          <p style={s.p}>{t.desc}</p>
          <div style={s.grid} className="pd-grid">
            {features.map((f, i) => (
              <div key={i} style={s.feat}>
                {f.icon}
                {f.text}
              </div>
            ))}
          </div>
          <div style={s.btnRow}>
            <button
              style={{ ...s.btn, ...s.primary }}
              onClick={() => setOpen(true)}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = 'translateY(-2px)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = 'translateY(0)')
              }
            >
              {t.view}
            </button>
            <button
              style={{ ...s.btn, ...s.outline }}
              onClick={download}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = 'translateY(-2px)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = 'translateY(0)')
              }
            >
              <IconDownload color="#0061FF" />
              {t.download}
            </button>
          </div>
        </div>
        {open && (
          <div
            style={s.modalOverlay}
            onClick={() => setOpen(false)}
          >
            <div
              style={s.modal}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                style={s.close}
                onClick={() => setOpen(false)}
              >
                <IconClose />
              </button>
              <iframe
                title="BusinessA Pitch Deck"
                src={pdfURL}
                style={s.iframe}
                loading="lazy"
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default BusinessPitchDeck
